import { toast } from 'vue-sonner'
import { callsService } from '../../services/index.js'
import { useCallFormatting } from './useCallFormatting.js'
import { resetCallState } from './useCallState.js'
import { rendreDemandeApresAnnulation, oublierPriseDeLAppel } from './useCallAssignment.js'
import { CallResult, CallCommentaireSource } from '../../constants/commercial-call.js'

/**
 * Composable pour sauvegarder un appel
 */
export function useCallSave(callState, stopTimer, beforeUnloadHandler) {
  const { formatCallNotes } = useCallFormatting()

  const saveCall = async (saveConfig) => {
    const {
      skipStatusUpdate = false,
      result = CallResult.ANSWERED,
      sendEmail = true,
      keepCallActive = false,
      commentaireSource = CallCommentaireSource.APPEL,
      extra = {}
    } = saveConfig || {}

    const updateStatusFn = callState.callbacks.value.updateStatus
    const loadHistoryFn = callState.callbacks.value.loadHistory
    const loadCommentsFn = callState.callbacks.value.loadComments
    
    // Garde synchrone anti double-submit : un re-clic avant le re-render
    // (bouton pas encore désactivé) créerait des appels en double.
    if (!callState.callInProgress.value || callState.savingCall.value) {
      return
    }

    callState.savingCall.value = true
    let echec = false
    try {
      const callDateISO = new Date().toISOString()
      const comments = callState.callComments.value ? callState.callComments.value.trim() : ''
      const notes = formatCallNotes(comments, callState.callDuration.value)
      
      // Un seul échange = une seule ligne d'appel. Revenir corriger l'analyse
      // du besoin met à jour l'appel existant ; sans cela, trois corrections
      // produiraient trois appels et trois commentaires pour une conversation.
      const dejaEnregistre = callState.savedCallId.value
      const payload = {
        quoteId: parseInt(callState.quoteId.value),
        callDate: callDateISO,
        direction: callState.callDirection.value,
        result: result,
        notes: notes,
        // La saisie seule, sans la durée que `notes` concatène toujours : c'est
        // elle qui décide, côté serveur, s'il y a lieu de créer un commentaire.
        commentaire: comments,
        commentaireSource,
        // §6.5 du panneau d'activité : la durée est persistée en colonne
        // exploitable en SQL, en plus du texte "MM:SS" concaténé dans `notes`
        // (format d'affichage historique, inchangé ci-dessus).
        durationSeconds: Number.isInteger(callState.callDuration.value) ? callState.callDuration.value : null,
        sendEmail: sendEmail,
        ...extra
      }

      if (dejaEnregistre) {
        await callsService.update(dejaEnregistre, payload)
      } else {
        const cree = await callsService.create(payload)
        if (cree?.id) callState.savedCallId.value = cree.id
      }
      
      if (!skipStatusUpdate && updateStatusFn && callState.quote.value?.status === 'demande_devis') {
        await updateStatusFn('appel_qualification')
      }
      
      if (loadHistoryFn) {
        await loadHistoryFn()
      }

      // Recharger les commentaires seulement si une saisie a pu en produire un.
      // `notes` porte toujours la durée : s'y fier rechargeait à chaque appel,
      // muet compris — c'est le raisonnement qui avait produit le bug corrigé.
      if (loadCommentsFn && comments) {
        await loadCommentsFn()
      }

    } catch (err) {
      echec = true
      toast.error('Erreur lors de l\'enregistrement de l\'appel')
      console.error(err)
      throw err
    } finally {
      callState.savingCall.value = false
      if (!keepCallActive && callState.callInProgress.value) {
        // L'appel se clôt sans issue enregistrée : la demande qu'il avait prise
        // redevient libre, comme à l'annulation.
        if (echec && !callState.savedCallId.value) rendreDemandeApresAnnulation()
        else oublierPriseDeLAppel()
        stopTimer()
        resetCallState()
        window.removeEventListener('beforeunload', beforeUnloadHandler)
      }
    }
  }

  return {
    saveCall
  }
}
